import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';
import { loginPathWithReturnTo, readPostLoginRedirect } from '../utils/navigationReturn';
import logoZra from '../../media/logo_zra.png';

const ForgotPassword = () => {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLink, setResetLink] = useState(null);
  const location = useLocation();
  const loginHref = loginPathWithReturnTo(readPostLoginRedirect(location));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setResetLink(null);
    const value = emailOrUsername?.trim();
    if (!value) {
      setError('Please enter your email or username.');
      return;
    }
    setLoading(true);
    try {
      const data = await authAPI.forgotPassword(value);
      setMessage(data.message || 'If an account exists with that email or username, you will receive reset instructions.');
      if (data.resetLink) {
        setResetLink(data.resetLink);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-lg">
        <div>
          <div className="flex justify-center">
            <img src={logoZra} alt="ZRA Logo" className="h-24 w-auto" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Forgot password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your email or username to receive a reset link
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              <p className="text-sm">{error}</p>
            </div>
          )}
          {message && !resetLink && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
              <p className="text-sm">{message}</p>
            </div>
          )}
          {resetLink && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded">
              <p className="text-sm font-medium mb-2">Reset link (for testing):</p>
              <a
                href={resetLink}
                className="text-sm text-primary-600 hover:underline break-all"
                target="_blank"
                rel="noopener noreferrer"
              >
                {resetLink}
              </a>
              <p className="text-xs mt-2 text-amber-700">Link expires in 30 minutes.</p>
            </div>
          )}
          <div>
            <label htmlFor="emailOrUsername" className="sr-only">
              Email or username
            </label>
            <input
              id="emailOrUsername"
              name="emailOrUsername"
              type="text"
              autoComplete="username email"
              required
              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Email or username"
              value={emailOrUsername}
              onChange={(e) => setEmailOrUsername(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="space-y-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Sending...' : 'Send reset link'}
            </button>
            <p className="text-center">
              <Link to={loginHref} className="text-sm text-primary-600 hover:text-primary-500">
                Back to sign in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
